import React, { useState, useEffect } from 'react'
import { Modal, Select, Upload } from 'antd'
import Cancel from 'assets/Images/close.svg'
import VectorImg from 'assets/Images/Vector.svg'
import { successMessage, failureMessage } from '../../api/global'
import { read, utils } from 'xlsx'
import CsvImg from 'assets/Images/pdf 1.svg'
import CheckImg from 'assets/Images/checked.svg'
import { useMutation, useQuery } from '@tanstack/react-query'
import {
  importFamilyFile,
  confirmImportData,
  importKidFile,
  confirmImportKidData,
  importAdultFile,
  confirmImportAdultData,
  getFamilies,
} from 'api/family'
import warningImg from 'assets/Images/ImportWarning.svg'
import plusImg from 'assets/Images/ImportPlus.svg'
import { GetRequestParams, Populate, RequestParms } from 'types/globalTypes'
import Loader from 'helper/Loader'
import './importFamilyStyle.scss'
const { Option } = Select
const ImportFamily = ({ visible, onClose, currentPage, status }) => {
  const prevRoute = localStorage.getItem('previousRoute')
  const [fileHeaders, setFileHeaders] = useState([])
  const [fileContent, setFileContent] = useState('')
  const [importType, setImportType] = useState('Family')
  const [memberType, setMemberType] = useState<'guardian' | 'kid' | 'adult'>('guardian')
  const [attributeMapping, setAttributeMapping] = useState({})
  const [isMappingModalVisible, setIsMappingModalVisible] = useState(false)
  const [isImportButtonDisabled, setIsImportButtonDisabled] = useState(true)
  const [isContinueButtonDisabled, setIsContinueButtonDisabled] = useState(true)
  const [isReviewModalVisible, setIsReviewModalVisible] = useState(false)
  const [apiResponse, setApiResponse] = useState(null)
  const [isResultModalVisible, setIsResultModalVisible] = useState(false)
  const [isLogsModalVisible, setIsLogsModalVisible] = useState(false)
  const [fileList, setFileList] = useState([])

  const [parms, setParms] = useState<
    RequestParms & GetRequestParams & Populate
  >({
    page: prevRoute.includes('profile') ? currentPage : 1,
    limit: 10,
    status: prevRoute.includes('profile') ? status : 'active',
    populate: ['guardians', 'kids', 'adults'],
  })
  const handleFileUpload = ({ file }) => {
    const reader = new FileReader()
    reader.onload = e => {
      const binaryStr = e.target.result
      const workbook = read(binaryStr, {
        type: 'binary',
        cellText: false,
        cellDates: true,
      })
      const sheetName = workbook.SheetNames[0]
      const sheet = workbook.Sheets[sheetName]
      const csvData = utils.sheet_to_csv(sheet, { rawNumbers: true })
      const firstLine = csvData.split('\n')[0]
      const parsedHeaders: string[] = []
      let cur = '', inQ = false
      for (const ch of firstLine) {
        if (ch === '"') { inQ = !inQ }
        else if (ch === ',' && !inQ) { parsedHeaders.push(cur.trim()); cur = '' }
        else { cur += ch }
      }
      parsedHeaders.push(cur.trim())
      setFileHeaders(parsedHeaders)
      setFileContent(csvData)
      setIsImportButtonDisabled(false)
      successMessage(`${file.name} file uploaded successfully`)
    }
    reader.onerror = () => {
      failureMessage(`${file.name} file upload failed.`)
    }

    if (
      file.type ===
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ) {
      reader.readAsBinaryString(file)
    } else {
      reader.readAsText(file)
    }
  }
  const uploadProps = {
    beforeUpload: file => {
      handleFileUpload({ file })
      return false
    },
  }
  const GUARDIAN_ATTRS = [
    { key: 'familyId', display: 'Family Id (omit to create new family)', required: false },
    { key: 'firstName', display: 'First Name', required: true },
    { key: 'middleName', display: 'Middle Name', required: false },
    { key: 'nickName', display: 'Nick Name', required: false },
    { key: 'lastName', display: 'Last Name', required: true },
    { key: 'gender', display: 'Gender', required: false },
    { key: 'dialCode', display: 'Dial Code', required: true },
    { key: 'phone', display: 'Phone', required: true },
    { key: 'allergies', display: 'Allergies (comma separated)', required: false },
    { key: 'medicalNotes', display: 'Medical Notes', required: false },
    { key: 'dob', display: 'Date of Birth', required: false },
    { key: 'relation', display: 'Relation', required: false },
    { key: 'email', display: 'Email', required: true },
    { key: 'password', display: 'Password', required: false },
    { key: 'title', display: 'Location Title', required: false },
    { key: 'apartment', display: 'Apartment', required: false },
    { key: 'street', display: 'Street', required: false },
    { key: 'city', display: 'City', required: false },
    { key: 'state', display: 'State', required: false },
    { key: 'country', display: 'Country', required: false },
    { key: 'zip', display: 'Zip', required: false },
  ]

  const KID_ATTRS = [
    { key: 'familyId', display: 'Family Id', required: true },
    { key: 'firstName', display: 'First Name', required: true },
    { key: 'middleName', display: 'Middle Name', required: false },
    { key: 'nickName', display: 'Nick Name', required: false },
    { key: 'lastName', display: 'Last Name', required: true },
    { key: 'gender', display: 'Gender', required: false },
    { key: 'dob', display: 'Date of Birth', required: false },
    { key: 'school', display: 'School', required: false },
    { key: 'grade', display: 'Grade', required: false },
    { key: 'allergies', display: 'Allergies (comma separated)', required: false },
    { key: 'medicalNotes', display: 'Medical Notes', required: false },
  ]

  const ADULT_ATTRS = [
    { key: 'familyId', display: 'Family Id', required: true },
    { key: 'firstName', display: 'First Name', required: true },
    { key: 'middleName', display: 'Middle Name', required: false },
    { key: 'nickName', display: 'Nick Name', required: false },
    { key: 'lastName', display: 'Last Name', required: true },
    { key: 'gender', display: 'Gender', required: false },
    { key: 'dob', display: 'Date of Birth', required: false },
    { key: 'dialCode', display: 'Dial Code', required: false },
    { key: 'phone', display: 'Phone', required: false },
    { key: 'email', display: 'Email', required: false },
    { key: 'relation', display: 'Relation', required: true },
    { key: 'allergies', display: 'Allergies (comma separated)', required: false },
    { key: 'medicalNotes', display: 'Medical Notes', required: false },
  ]

  const attributeOptions =
    memberType === 'kid' ? KID_ATTRS : memberType === 'adult' ? ADULT_ATTRS : GUARDIAN_ATTRS
  const handleBack = () => {
    resetMapping()
    setIsMappingModalVisible(false)
  }
  const resetMapping = () => {
    const newMapping = {}
    attributeOptions.forEach(attr => {
      newMapping[attr.key] = 'Do not import'
    })
    setAttributeMapping(newMapping)
  }
  useEffect(() => {
    const areAllRequiredAttributesMapped = attributeOptions
      .filter(attr => attr.required)
      .every(
        attr =>
          attributeMapping[attr.key] &&
          attributeMapping[attr.key] !== 'Do not import',
      )

    setIsContinueButtonDisabled(!areAllRequiredAttributesMapped)
  }, [attributeMapping])
  const handleAttributeChange = (attribute, value) => {
    setAttributeMapping({ ...attributeMapping, [attribute]: value })
  }
  const handleReview = () => {
    setIsMappingModalVisible(false)
    setIsReviewModalVisible(true)
  }
  const getImportFn = () => {
    if (memberType === 'kid') return importKidFile
    if (memberType === 'adult') return importAdultFile
    return importFamilyFile
  }

  const getConfirmFn = () => {
    if (memberType === 'kid') return confirmImportKidData
    if (memberType === 'adult') return confirmImportAdultData
    return confirmImportData
  }

  const importFamilyMutation = useMutation({
    mutationFn: async ({ payload }: any) => {
      return await getImportFn()(payload)
    },
    onSuccess: data => {
      if (data) {
        setIsReviewModalVisible(false)
        setApiResponse(data)
        setIsResultModalVisible(true)
      }
    },
  })
  const handleImport = () => {
    const parseCSVLine = (line: string): string[] => {
      const result: string[] = []
      let current = ''
      let inQuotes = false
      for (let i = 0; i < line.length; i++) {
        const char = line[i]
        if (char === '"') {
          inQuotes = !inQuotes
        } else if (char === ',' && !inQuotes) {
          result.push(current.trim())
          current = ''
        } else {
          current += char
        }
      }
      result.push(current.trim())
      return result
    }

    const lines = fileContent.split('\n')
    const headers = parseCSVLine(lines[0])
    const mappedData = []
    for (let i = 1; i < lines.length; i++) {
      if (!lines[i].trim()) continue
      const values = parseCSVLine(lines[i])
      const mappedRow = {}
      let hasValidData = false
      attributeOptions.forEach(attr => {
        const csvColumn = attributeMapping[attr.key]
        if (csvColumn && csvColumn !== 'Do not import') {
          const columnIndex = headers.indexOf(csvColumn)
          if (columnIndex !== -1) {
            let value = values[columnIndex] ?? ''
            if (
              attr.key === 'phone' &&
              (value.includes('E') || value.includes('e'))
            ) {
              value = parseFloat(value).toFixed(0)
            }
            // Skip empty optional fields so the backend never receives e.g. familyId: ""
            if (value === '' && !attr.required) return
            mappedRow[attr.key] = value
            if (value) {
              hasValidData = true
            }
          }
        }
      })
      if (hasValidData) {
        mappedData.push(mappedRow)
      }
    }
    importFamilyMutation.mutate({ payload: mappedData })
  }
  const { isLoading, isFetching, data, refetch } = useQuery(
    ['getFamiles', parms],
    () => getFamilies(parms),
    { enabled: false },
  )
  const CancelImport = () => {
    const payload = {
      validationId: apiResponse?.validationId,
      confirm: false,
    }
    confirmImportMutation.mutate({ payload: payload })
    setIsResultModalVisible(false)
    onClose()
  }
  const confirmImportMutation = useMutation({
    mutationFn: async ({ payload }: any) => {
      return await getConfirmFn()(payload)
    },
    onSuccess: data => {
      if (data) {
        successMessage(data)
        setIsResultModalVisible(false)
        onClose()
        refetch()
      }
    },
  })
  const handleComplete = () => {
    const payload = {
      validationId: apiResponse?.validationId,
      confirm: true,
    }

    confirmImportMutation.mutate({ payload: payload })
  }
  const downloadCSV = () => {
    const logs = apiResponse?.logs || []
    const headers = ['Row', 'Message']
    const rows = logs.map(log => [log.row + 1, log.msg])
    let csvContent =
      'data:text/csv;charset=utf-8,' +
      [headers, ...rows].map(e => e.join(',')).join('\n')
    const encodedUri = encodeURI(csvContent)
    const link = document.createElement('a')
    link.setAttribute('href', encodedUri)
    link.setAttribute('download', 'logs.csv')
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  return (
    <>
      <Modal
        open={visible}
        footer={null}
        onCancel={onClose}
        className="import-modal"
      >
        <div className="popup-header">
          <h2 className="importText">Import Data</h2>
          <img src={Cancel} alt="close button" onClick={onClose} />
        </div>
        <div style={{ marginBottom: 16, marginTop: 10 }}>
          <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>
            Import Type
          </label>
          <Select
            value={memberType}
            style={{ width: '100%' }}
            onChange={(val: 'guardian' | 'kid' | 'adult') => {
              setMemberType(val)
              setIsImportButtonDisabled(true)
              setFileList([])
              setFileContent('')
              setFileHeaders([])
            }}
          >
            <Option value="guardian">Guardian</Option>
            <Option value="kid">Kid</Option>
            <Option value="adult">Authorized Adult</Option>
          </Select>
        </div>
        <Upload.Dragger
          {...uploadProps}
          accept=".csv,.xlsx"
          fileList={fileList}
          onChange={info => setFileList(info.fileList)}
        >
          <p className="ant-upload-drag-icon">
            <img alt="VectorImg" src={VectorImg} />
          </p>
          <p className="dragText">
            Drag and drop document here or
            <b className="uploadText"> upload document</b>
          </p>
          <p className="ant-upload-hint" style={{ marginTop: '10px' }}>
            CSV or XLSX formats
          </p>
        </Upload.Dragger>
        <div className="flex justify-end w-full mt-20">
          <button
            className="prim-sm-btn"
            style={{ background: '#EBEDF1', color: 'black' }}
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="prim-sm-btn"
            disabled={isImportButtonDisabled}
            onClick={() => setIsMappingModalVisible(true)}
          >
            Import
          </button>
        </div>
      </Modal>

      <Modal
        open={isMappingModalVisible}
        footer={null}
        onCancel={handleBack}
        className="mapping-modal"
      >
        <div>
          <div className="popup-header-2">
            <h2>Customize your attributes</h2>
            <img src={Cancel} alt="close button" onClick={handleBack} />
          </div>
          <p className="attributeText">
            Select the correct attribute for each column you’d like to import,
            or create a new one.
          </p>

          <p className="schema-Text">Schema mapping</p>
          <div className="schema-mapping-header">
            <span className="attribute-header">Attribute</span>
            <span className="csv-column-header">CSV column</span>
          </div>
          <div className="schema-mapping h-25rem overflow-auto">
            {attributeOptions.map(attr => (
              <div key={attr.key} className="mapping-row">
                <div className="fleximg">
                  <img
                    alt="csv image"
                    src={CsvImg}
                    style={{ marginRight: '5px' }}
                  />
                  <span
                    className={`attribute-label position-relative ${
                      attr.required ? 'required-input' : ''
                    }`}
                  >
                    {attr.display}
                  </span>
                </div>
                <Select
                  value={attributeMapping[attr.key] || 'Do not import'}
                  className="csv-column-select"
                  onChange={value => handleAttributeChange(attr.key, value)}
                >
                  <Option value="Do not import">Do not import</Option>
                  {fileHeaders.map(header => (
                    <Option key={header} value={header}>
                      {header}
                    </Option>
                  ))}
                </Select>
              </div>
            ))}
          </div>
          <div className="flex justify-end w-full mt-20">
            <button
              className="prim-sm-btn"
              style={{ background: '#EBEDF1', color: 'black' }}
              onClick={handleBack}
            >
              Back
            </button>
            <button
              className="prim-sm-btn"
              disabled={isContinueButtonDisabled}
              onClick={handleReview}
            >
              Continue
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        open={isReviewModalVisible}
        footer={null}
        onCancel={() => setIsReviewModalVisible(false)}
        className="review-modal"
      >
        <div>
          <div className="popup-header-2">
            <h2>Review values</h2>
            <img
              src={Cancel}
              alt="close button"
              onClick={() => {
                setIsReviewModalVisible(false)
                setIsMappingModalVisible(true)
              }}
            />
          </div>
          <p className="attributeText">
            We’va created the values we found in your data. Check that the
            values are correct and resolve any that are invalid.
          </p>
          <p className="schema-Text">Schema mapping</p>
          <div className="schema-mapping-header">
            <span className="attribute-header">Attribute</span>
            <span className="csv-column-header">CSV column</span>
          </div>
          <div className="schema-mapping h-25rem overflow-auto">
            {Object.keys(attributeMapping).map(
              key =>
                attributeMapping[key] !== 'Do not import' && (
                  <div key={key} className="mapping-row">
                    <div className="fleximg-2">
                      <img
                        alt="check image"
                        src={CheckImg}
                        style={{ marginRight: '10px' }}
                      />
                      <img
                        alt="csv image"
                        src={CsvImg}
                        style={{ marginRight: '8px' }}
                      />

                      <span className="attribute-label position-relative">
                        {
                          attributeOptions.find(attr => attr.key === key)
                            .display
                        }
                      </span>
                    </div>
                    <span className="csv-column-label">
                      {attributeMapping[key].replace(/"/g, '')}
                    </span>
                  </div>
                ),
            )}
          </div>
          <div className="flex justify-end w-full mt-20">
            <button
              className="prim-sm-btn"
              style={{
                background: '#EBEDF1',
                color: 'black',
                marginRight: '10px',
              }}
              onClick={() => {
                setIsReviewModalVisible(false)
                setIsMappingModalVisible(true)
              }}
            >
              Back
            </button>
            <button className="prim-sm-btn" onClick={handleImport}>
              {importFamilyMutation.isLoading ? <Loader /> : 'Continue'}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        open={isResultModalVisible}
        onCancel={() => setIsResultModalVisible(false)}
        footer={null}
        className="completeModal"
      >
        <div className="popup-header-2">
          <h2>Complete import</h2>
          <img
            src={Cancel}
            alt="close button"
            onClick={() => {
              setIsResultModalVisible(false)
            }}
          />
        </div>
        <div className="d-flex mt-20">
          <div className="recordFill ">
            <div className="flex">
              <img src={warningImg} alt="warningImg" />
              <span className="failedText">{apiResponse?.stats?.invalid}</span>
            </div>
            <span className="text">Records failed</span>
          </div>
          <div className="recordFill" style={{ marginLeft: '10px' }}>
            <div className="flex">
              <img src={plusImg} alt="plusImg" />
              <span className="SuccessText">{apiResponse?.stats?.valid}</span>
            </div>
            <span className="text">Records will be added</span>
          </div>
        </div>
        <div className="flex justify-end w-full mt-20">
          {apiResponse?.stats?.invalid > 0 && (
            <>
              <button
                className="prim-sm-btn"
                style={{
                  background: 'transparent',
                  border: '1px solid #2BA7CA',
                  color: '#2BA7CA',
                  marginRight: '10px',
                }}
                onClick={CancelImport}
              >
                Cancel
              </button>
              <button
                className="prim-sm-btn"
                style={{
                  background: 'transparent',
                  border: '1px solid #2BA7CA',
                  color: '#2BA7CA',
                  marginRight: '10px',
                }}
                onClick={() => {
                  setIsLogsModalVisible(true)
                }}
              >
                View logs
              </button>
            </>
          )}
          <button className="prim-sm-btn" onClick={handleComplete}>
            {confirmImportMutation.isLoading ? <Loader /> : 'Complete'}
          </button>
        </div>
      </Modal>

      <Modal open={isLogsModalVisible} footer={null} className="logsModal">
        <div className="popup-header-2">
          <h2>Logs</h2>
          <img
            src={Cancel}
            alt="close button"
            onClick={() => {
              setIsLogsModalVisible(false)
            }}
          />
        </div>

        <div className="schema-mapping h-25rem overflow-auto">
          <table>
            <thead>
              <tr>
                <th colSpan={1}></th>
                <th>Row</th>
                <th colSpan={10}></th>
                <th>Message</th>
              </tr>
            </thead>
            <tbody>
              {apiResponse?.logs.map((log, index) => (
                <tr key={index}>
                  <td colSpan={1}></td>
                  <td style={{ textAlign: 'center' }} className="errorMessage">
                    {log.row + 1}
                  </td>
                  <td colSpan={10}></td>
                  <td className="errorMessage">{log.msg}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex justify-end w-full mt-20">
          <button
            className="prim-sm-btn"
            style={{
              background: 'transparent',
              border: '1px solid #2BA7CA',
              color: '#2BA7CA',
              marginRight: '10px',
            }}
            onClick={() => {
              setIsLogsModalVisible(false)
            }}
          >
            Back
          </button>
          <button className="prim-sm-btn" onClick={downloadCSV}>
            Download
          </button>
        </div>
      </Modal>
    </>
  )
}
export default ImportFamily
